<script setup lang="ts">
import { computed, ref } from 'vue'

interface Props {
  color?:
    | 'V_CAPITAL'
    | 'V_MANAGEMENT_FEE'
    | 'V_OTHER_FEE'
    | 'V_COMMITMENT'
    | 'V_DISTRIBUTION_RETURN_OF_CAPITAL'
    | 'V_DISTRIBUTION_PREFERRED_RETURN'
    | 'V_DISTRIBUTION_PROFIT'
    | 'V_DISTRIBUTION_CARRIED_INTEREST'
    | 'V_DISTRIBUTION_FEE'
    | 'V_UNPAID'
    | 'V_PAID'
  dot?: boolean
  focusable?: boolean
  size?: 'xs' | 'sm'
}

const props = withDefaults(defineProps<Props>(), {
  color: 'V_CAPITAL',
  dot: true,
  focusable: false,
  size: 'sm', // bumped up one from old default
})

const palette: Record<string, string> = {
  V_CAPITAL: '#3b88af',
  V_MANAGEMENT_FEE: '#7d8ad0',
  V_OTHER_FEE: '#7d8ad0',
  V_COMMITMENT: '#D2D5DB',
  V_DISTRIBUTION_RETURN_OF_CAPITAL: '#408F93',
  V_DISTRIBUTION_PREFERRED_RETURN: '#4FB8A9',
  V_DISTRIBUTION_PROFIT: '#46B37D',
  V_DISTRIBUTION_CARRIED_INTEREST: '#7d8ad0',
  V_DISTRIBUTION_FEE: '#7d8ad0',
  V_UNPAID: '#d97706',
  V_PAID: '#408F93',
}

const dotColor = computed(() => {
  const hex = palette[props.color]
  return {
    '--dot-color': hex,
    '--bg-color': hex + '20',
    '--text-color': hex,
  }
})

const showDot = computed(() => props.dot !== false)

// -----------------------------------------------------------------------------
// Focus state (for focusable badges)
// -----------------------------------------------------------------------------

const focused = ref(false)

const focus = () => {
  if (!props.focusable) return
  focused.value = true
}
const clearFocus = () => {
  if (!props.focusable) return
  focused.value = false
}

// -----------------------------------------------------------------------------
// Size variants
// -----------------------------------------------------------------------------

const sizeClasses = computed(() => {
  if (props.size === 'xs') {
    return {
      text: 'text-xs',
      padding: 'px-2 py-1',
      dot: 'h-1.5 w-1.5',
      space: 'space-x-1.5',
    }
  }
  // sm (new default, one size up)
  return {
    text: 'text-sm',
    padding: 'px-2 py-1',
    dot: 'h-1.5 w-1.5',
    space: 'space-x-1.5',
  }
})
</script>

<template>
  <span
    class="inline-flex cursor-default items-center rounded-md border font-medium"
    :class="[sizeClasses.text, sizeClasses.padding, sizeClasses.space]"
    :style="{
      color: dotColor['--text-color'],
      borderColor: dotColor['--bg-color'],
    }"
    @mouseenter="focus"
    @mouseleave="clearFocus"
  >
    <span v-if="showDot">
      <svg
        class="transition-transform duration-100 ease-out"
        :class="sizeClasses.dot"
        viewBox="0 0 6 6"
        aria-hidden="true"
        :style="{
          transform: focused ? 'scale(1.2)' : 'scale(1)',
        }"
      >
        <circle cx="3" cy="3" r="3" :fill="dotColor['--dot-color']" />
      </svg>
    </span>
    <span class="whitespace-nowrap">
      <slot />
    </span>
  </span>
</template>
